import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { VersionBadge } from './VersionBadge';

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const getUserInitials = () => {
    if (!user?.nome) return 'US';
    const names = user.nome.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  return (
    <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden bg-background-light font-display text-slate-800">
      {/* Header */}
      <header className="sticky top-0 z-20 w-full border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-8">
            {/* Logo */}
            <div className="flex items-center gap-3 text-slate-800">
              <div className="size-7 text-primary">
                <svg fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21.435 15.51a8.318 8.318 0 0 1-3.213 1.834l-7.22-7.22 1.834-3.213a8.318 8.318 0 0 1 8.599 8.599Zm-18.87 2.155a8.318 8.318 0 0 1 8.599-8.599l1.834 3.213-7.22 7.22a8.318 8.318 0 0 1-3.213-1.834ZM12 24a12 12 0 1 1 0-24 12 12 0 0 1 0 24Z"></path>
                </svg>
              </div>
              <h1 className="text-lg font-bold">AirTrust</h1>
            </div>

            {/* Navigation (text-only, no icons) */}
            <nav className="hidden items-center gap-2 md:flex">
              <Link
                to="/"
                className={`flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium ${
                  isActive('/')
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Painel
              </Link>
              <Link
                to="/funcionarios"
                className={`flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium ${
                  isActive('/funcionarios')
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Funcionários
              </Link>
              <Link
                to="/qualificacoes"
                className={`flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium ${
                  isActive('/qualificacoes')
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Qualificações
              </Link>
              <Link
                to="/simuladores"
                className={`flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium ${
                  isActive('/simuladores')
                    ? 'bg-primary/10 text-primary font-semibold'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                Simuladores
              </Link>
            </nav>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <button
                className="flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                title="Notificações"
              >
                <span className="material-symbols-outlined text-xl">notifications</span>
              </button>
              <button
                className="flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200"
                title="Configurações"
                onClick={() => navigate('/configuracoes')}
              >
                <span className="material-symbols-outlined text-xl">settings</span>
              </button>
            </div>
            <div
              className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-sm font-bold text-slate-600 cursor-pointer"
              title={user?.nome || 'Usuário'}
              onClick={logout}
            >
              {getUserInitials()}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="mx-auto w-full max-w-screen-2xl flex-1 px-4 py-8 sm:px-6 lg:px-8">
        {children}
      </main>

      {/* Version Badge */}
      <footer className="mt-auto">
        <VersionBadge />
      </footer>
    </div>
  );
}
